import { useRef, useState, useEffect, useCallback } from 'react';
import EmailEditor from 'react-email-editor';
import axios from 'axios';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme as useMuiTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';

const EditorDialog = ({
    open,
    onClose,
    isEditing,
    form,
    setForm,
    formErrors,
    setFormErrors,
    isDarkMode,
    currentDesign,
    onSave,
}) => {
    const emailEditorRef = useRef(null);
    const [editorReady, setEditorReady] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const pendingDesignRef = useRef(null);
    const lastLoadedDesignRef = useRef(null);
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

    const onEditorReady = useCallback(() => {
        setEditorReady(true);
        const editor = emailEditorRef.current?.editor;

        // Registrar callback de subida de imágenes
        editor?.registerCallback('image', async (file, done) => {
            try {
                const formData = new FormData();
                formData.append('file', file.attachments[0]);
                const { data } = await axios.post('template-email/upload-image', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                done({ progress: 100, url: data.url });
            } catch (error) {
                console.error('Error subiendo imagen:', error);
                done({ progress: 0 });
            }
        });
    }, []);

    // Helper: guardar copia del design que se carga en el editor
    const loadDesignAndStore = useCallback((editor, design) => {
        lastLoadedDesignRef.current = JSON.parse(JSON.stringify(design));
        editor.loadDesign(design);
    }, []);

    useEffect(() => {
        if (editorReady && open) {
            const editor = emailEditorRef.current?.editor;
            if (!editor) return;
            if (pendingDesignRef.current) {
                // Cargar design pendiente (del botón agregar Text)
                loadDesignAndStore(editor, pendingDesignRef.current);
                pendingDesignRef.current = null;
            } else if (isEditing && currentDesign) {
                loadDesignAndStore(editor, currentDesign);
            } else if (!isEditing) {
                lastLoadedDesignRef.current = null;
                editor.loadBlank({});
            }
        }
    }, [editorReady, open, isEditing, currentDesign, loadDesignAndStore]);

    // Reset editorReady when dialog closes
    useEffect(() => {
        if (!open) setEditorReady(false);
    }, [open]);

    const validate = () => {
        const errors = {};
        if (!form.nombre.trim()) errors.nombre = 'El nombre es requerido';
        return errors;
    };

    const handleSave = () => {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        emailEditorRef.current?.editor?.saveDesign((design) => {
            emailEditorRef.current?.editor?.exportHtml((data) => {
                const { html } = data;
                onSave(design, html);
            });
        });
    };

    const handleAddTextBlock = () => {
        const editor = emailEditorRef.current?.editor;
        if (!editor) return;

        // Estrategia: usar el design original (que tiene type:'text') como base.
        // saveDesign convierte text→paragraph y modifica el HTML interno, lo que
        // hace que Unlayer pierda las opciones del Text tool (font family, font size, alignment).
        // Al usar el design original que nunca pasó por saveDesign, los bloques
        // mantienen type:'text' y Unlayer los renderiza con el toolbar completo.
        editor.saveDesign((savedDesign) => {
            // Usar el design original guardado antes de que Unlayer lo toque
            const baseDesign = lastLoadedDesignRef.current
                ? JSON.parse(JSON.stringify(lastLoadedDesignRef.current))
                : JSON.parse(JSON.stringify(savedDesign));

            // Construir mapa de contenido editado por id (del saveDesign)
            const editedTextMap = {};
            savedDesign.body.rows.forEach(row => {
                row.columns?.forEach(col => {
                    col.contents?.forEach(content => {
                        if (content.values?.text) {
                            editedTextMap[content.id] = content.values.text;
                        }
                    });
                });
            });

            // Aplicar las ediciones de texto del usuario al design original
            baseDesign.body.rows.forEach(row => {
                row.columns?.forEach(col => {
                    col.contents?.forEach(content => {
                        if (editedTextMap[content.id] !== undefined) {
                            content.values.text = editedTextMap[content.id];
                        }
                    });
                });
            });

            // Agregar nuevo bloque Text al final
            const rowUid = Math.random().toString(36).slice(2);
            const colUid = Math.random().toString(36).slice(2);
            const ctUid = Math.random().toString(36).slice(2);
            const newRow = {
                id: `r_${rowUid}`,
                cells: [1],
                columns: [{
                    id: `c_${colUid}`,
                    contents: [{
                        id: `ct_${ctUid}`,
                        type: 'text',
                        values: {
                            containerPadding: '20px 40px',
                            anchor: '',
                            textAlign: 'left',
                            lineHeight: '160%',
                            linkStyle: { inherit: true, linkColor: '#0000ee', linkHoverColor: '#0000ee', linkUnderline: true, linkHoverUnderline: true },
                            hideDesktop: false,
                            displayCondition: null,
                            _meta: { htmlID: `u_content_${ctUid}`, htmlClassNames: 'u_content_text' },
                            selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
                            text: '<p style="font-size: 14px; color: #333333; line-height: 160%;">Escribí tu texto aquí...</p>'
                        }
                    }],
                    values: { backgroundColor: '', padding: '0px', border: {}, borderRadius: '0px', _meta: { htmlID: `u_column_${colUid}`, htmlClassNames: 'u_column' } }
                }],
                values: {
                    displayCondition: null,
                    columns: false,
                    backgroundColor: '#ffffff',
                    columnsBackgroundColor: '',
                    backgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'cover', position: 'center' },
                    padding: '0px',
                    anchor: '',
                    hideDesktop: false,
                    _meta: { htmlID: `u_row_${rowUid}`, htmlClassNames: 'u_row' },
                    selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true
                }
            };
            baseDesign.body.rows.push(newRow);

            // Forzar remontaje del editor para carga limpia
            pendingDesignRef.current = baseDesign;
            setEditorReady(false);
            setEditorKey(k => k + 1);
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    width: isMobile ? '100vw' : '95vw',
                    height: isMobile ? '100vh' : '95vh',
                    maxHeight: isMobile ? '100vh' : '95vh',
                    borderRadius: isMobile ? 0 : 3,
                    background: isDarkMode ? '#1a1a2e' : '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: isDarkMode ? '#13132a' : '#fff',
                    borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                    py: isMobile ? 1 : 1.5,
                    px: isMobile ? 2 : 3,
                }}
            >
                <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    component="span"
                    sx={{ fontWeight: 700, color: isDarkMode ? '#e0e0e0' : '#1a1a2e' }}
                >
                    {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 1.5 : 2,
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1 : 1.5,
                    background: isDarkMode ? '#13132a' : '#fff',
                    borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                }}
            >
                <TextField
                    label="Nombre de la plantilla *"
                    value={form.nombre}
                    onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                    error={!!formErrors.nombre}
                    helperText={formErrors.nombre}
                    size="small"
                    sx={{ width: isMobile ? '100%' : isTablet ? 220 : 280 }}
                />
                <TextField
                    label="Descripción (opcional)"
                    value={form.descripcion}
                    onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                    size="small"
                    sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}
                />
            </Box>

            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '& > div': { flex: 1, display: 'flex', flexDirection: 'column' },
                    '& iframe': { flex: 1, minHeight: '100% !important' },
                }}
            >
                {/* Overlay de carga mientras el editor se reinicia */}
                {!editorReady && editorKey > 0 && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isDarkMode ? 'rgba(26,26,46,0.85)' : 'rgba(255,255,255,0.85)',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" sx={{ color: isDarkMode ? '#9e9e9e' : '#666' }}>
                            Agregando bloque Text...
                        </Typography>
                    </Box>
                )}
                {editorReady && (
                    <Tooltip title="Agregar bloque Text" placement="left">
                        <Fab
                            size="medium"
                            onClick={handleAddTextBlock}
                            sx={{
                                position: 'absolute',
                                bottom: 24,
                                right: 24,
                                zIndex: 10,
                                background: 'linear-gradient(135deg, #0364ff, #1a73e8)',
                                color: '#fff',
                                boxShadow: '0 4px 14px rgba(3,100,255,0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #0250cc, #1565c0)',
                                    boxShadow: '0 6px 20px rgba(3,100,255,0.5)',
                                },
                            }}
                        >
                            <TextFieldsIcon />
                        </Fab>
                    </Tooltip>
                )}
                <EmailEditor
                    key={editorKey}
                    ref={emailEditorRef}
                    onReady={onEditorReady}
                    minHeight="100%"
                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    options={{
                        displayMode: 'email',
                        locale: 'es-ES',
                        customCSS: [`${window.location.origin}/unlayer-custom.css`],
                        appearance: {
                            theme: isDarkMode ? 'dark' : 'light',
                            panels: { tools: { dock: 'left' } },
                        },
                        features: {
                            textEditor: { spellChecker: true },
                        },
                        tools: {
                            text: {
                                enabled: true,
                            },
                            paragraph: {
                                properties: {
                                    textAlign: {
                                        editor: { defaultValue: 'left' },
                                    },
                                    fontSize: {
                                        editor: { defaultValue: '14px' },
                                    },
                                    lineHeight: {
                                        editor: { defaultValue: '160%' },
                                    },
                                    containerPadding: {
                                        editor: { defaultValue: '20px 40px' },
                                    },
                                    fontFamily: {
                                        editor: {
                                            defaultValue: { value: 'arial,helvetica,sans-serif' },
                                        },
                                    },
                                    color: {
                                        editor: { defaultValue: '#333333' },
                                    },
                                },
                            },
                        },
                    }}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    background: isDarkMode ? '#13132a' : '#fff',
                    borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1 : 1.5,
                    gap: 1,
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    fullWidth={isMobile}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!editorReady}
                    fullWidth={isMobile}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #0364ff, #1a73e8)',
                        boxShadow: '0 4px 12px rgba(3,100,255,0.3)',
                    }}
                >
                    {isEditing ? 'Guardar cambios' : 'Guardar plantilla'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditorDialog;
